"use client";

import { motion } from "framer-motion";
import { withPhoneLinks } from "@/components/PhoneText";

interface AdditionalSection {
  heading: string;
  content?: string;
  listItems?: Array<{ label: string; text: string }>;
  afterContent?: string;
  cta?: { text: string; href: string };
}

import type { Locale } from "./templates/ServicePageTemplate";

const seoT = {
  sv: {
    whyHeading: "Varför boka med Ungdomskraft?",
    whyText: "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp — du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som får lära sig allt från kundkontakt och logistik till fysiskt arbete och ansvarsfullt miljöarbete.",
  },
  en: {
    whyHeading: "Why book with Ungdomskraft?",
    whyText: "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices.",
  },
};

interface SeoContentSectionProps {
  mainHeading: string;
  mainText: string;
  preparationHeading: string;
  preparationText: string;
  additionalSections?: AdditionalSection[];
  locale?: Locale;
}

export default function SeoContentSection({
  mainHeading,
  mainText,
  preparationHeading,
  preparationText,
  additionalSections,
  locale = "sv",
}: SeoContentSectionProps) {
  const l = seoT[locale];
  return (
    <section className="py-8 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark">
            {mainHeading}
          </h2>
          {mainText.split("\n\n").map((p, i) => (
            <p key={i} className="mt-3 text-sm text-gray-600 leading-relaxed">
              {withPhoneLinks(p)}
            </p>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mt-8"
        >
          <h3 className="text-base sm:text-lg font-bold text-brand-dark">
            {preparationHeading}
          </h3>
          {preparationText.split("\n\n").map((p, i) => (
            <p key={i} className="mt-3 text-sm text-gray-600 leading-relaxed">
              {withPhoneLinks(p)}
            </p>
          ))}
        </motion.div>

        {additionalSections?.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.05 + idx * 0.05 }}
            className="mt-8"
          >
            <h3 className="text-base sm:text-lg font-bold text-brand-dark">
              {section.heading}
            </h3>
            {section.content &&
              section.content.split("\n\n").map((p, i) => (
                <p
                  key={i}
                  className="mt-3 text-sm text-gray-600 leading-relaxed"
                >
                  {withPhoneLinks(p)}
                </p>
              ))}
            {section.listItems && (
              <ul className="mt-3 space-y-3 text-sm text-gray-600 leading-relaxed">
                {section.listItems.map((item, i) => (
                  <li key={i}>
                    <span className="font-medium text-brand-dark">
                      {item.label}
                    </span>{" "}
                    {withPhoneLinks(item.text)}
                  </li>
                ))}
              </ul>
            )}
            {section.afterContent &&
              section.afterContent.split("\n\n").map((p, i) => (
                <p
                  key={i}
                  className="mt-3 text-sm text-gray-600 leading-relaxed"
                >
                  {withPhoneLinks(p)}
                </p>
              ))}
            {section.cta && (
              <a
                href={section.cta.href}
                className="mt-6 inline-block bg-brand-secondary text-white font-bold text-sm px-8 py-3 rounded-xl hover:brightness-110 transition-all"
              >
                {section.cta.text}
              </a>
            )}
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8"
        >
          <h3 className="text-base sm:text-lg font-bold text-brand-dark">
            {l.whyHeading}
          </h3>
          <p className="mt-3 text-sm text-gray-600 leading-relaxed">
            {l.whyText}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
